import { useRef } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useLang } from '@/i18n/LanguageContext';
import { SITE } from '@/content/site';
import MaskLines from '@/components/MaskLines';
import VideoFacade from '@/components/VideoFacade';

const VIDEOS = [...SITE.videos.stories, ...SITE.videos.words];

export default function Videos() {
  const { t } = useLang();
  const trackRef = useRef(null);

  // scorre di una card alla volta
  const move = (dir) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.firstElementChild;
    const step = card ? card.getBoundingClientRect().width + 20 : track.clientWidth * 0.8;
    track.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  return (
    <section id="video" className="py-28 md:py-36" data-testid="section-video">
      <div className="px-5 md:px-10">
        <p className="font-mono-label text-[var(--pz-green)]">{t.stories.kicker}</p>
        <h2 className="font-display mt-6 text-[11.5vw] leading-[0.9] md:text-[6.5vw]">
          <MaskLines lines={t.stories.headline} />
        </h2>
        <p className="mt-10 max-w-2xl text-base text-white/65 md:text-lg">{t.stories.body}</p>
        <ul className="mt-8 flex flex-wrap gap-2.5" data-testid="video-topics">
          {t.stories.topics.map((topic) => (
            <li key={topic} className="font-mono-label rounded-full border border-white/25 px-4 py-2">
              {topic}
            </li>
          ))}
        </ul>
      </div>

      <div
        ref={trackRef}
        data-testid="video-carousel"
        className="mt-16 flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-px-5 px-5 pb-4 md:scroll-px-10 md:px-10 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {VIDEOS.map((video) => (
          <div key={video.id} className="w-[85vw] shrink-0 snap-start md:w-[42vw]">
            <VideoFacade id={video.id} title={video.title} />
          </div>
        ))}
      </div>

      <div className="mt-8 flex items-center justify-between px-5 md:px-10">
        <a
          data-testid="video-channel-link"
          href={SITE.socials.find((s) => s.id === 'youtube').url}
          target="_blank"
          rel="noopener noreferrer"
          className="link-underline font-mono-label text-white/70 hover:text-white"
        >
          {t.stories.watch} ↗
        </a>
        <div className="flex gap-3">
          {[
            [-1, ArrowLeft, 'video-prev'],
            [1, ArrowRight, 'video-next'],
          ].map(([dir, Icon, id]) => (
            <button
              key={id}
              data-testid={id}
              onClick={() => move(dir)}
              aria-label={dir < 0 ? '←' : '→'}
              className="flex h-12 w-12 items-center justify-center border border-white/25 text-white/70 transition-colors hover:border-[var(--pz-green)] hover:text-[var(--pz-green)]"
            >
              <Icon size={18} />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
